export interface RefreshMatchCandidate {
  personId: string
  firstName: string
  lastName: string
  dateOfBirth?: string
  ssnLast4?: string
  confidence: number
  matchType: string
  matchedFields: string[]
  previousApplications?: readonly unknown[]
}

export interface MatchRefreshContext {
  sourceId: string
  section: "accountHolder" | "coCustomer"
  revision: number
}

/** A refresh response may only mutate the exact editor revision that submitted it. */
export function canApplyMatchRefresh(
  submitted: MatchRefreshContext,
  current: MatchRefreshContext,
  creationLocked: boolean,
): boolean {
  return !creationLocked && submitted.sourceId === current.sourceId &&
    submitted.section === current.section && submitted.revision === current.revision
}

/** Accept a response against live refs and fence any older pending save response. */
export function acceptMatchRefresh(
  submitted: MatchRefreshContext,
  source: { current: string | undefined },
  revision: { current: number },
  creationLocked: { current: boolean },
): boolean {
  if (!canApplyMatchRefresh(submitted, {
    sourceId: source.current ?? "",
    section: submitted.section,
    revision: revision.current,
  }, creationLocked.current)) return false
  revision.current += 1
  return true
}

/**
 * Keep source-side evidence for candidates that were already in the document. The server enriches
 * newly discovered candidates with the same authorized previous-application projection.
 */
export function reconcileRefreshedMatches<T extends RefreshMatchCandidate>(
  existing: readonly T[],
  refreshed: readonly T[],
): T[] {
  const existingById = new Map(existing.map(candidate => [candidate.personId, candidate]))
  return refreshed.map(candidate => {
    const prior = existingById.get(candidate.personId)
    if (candidate.previousApplications?.length || !prior?.previousApplications?.length) return candidate
    return { ...candidate, previousApplications: prior.previousApplications }
  })
}
